import { createContext, useContext, useEffect, useState } from 'react'

/**
 * ThemeContext — app-wide light/dark theme state.
 *
 * The chosen theme is persisted to localStorage and exposed via `theme` +
 * `toggleTheme`, but it is NOT stamped onto <html>/<body>. Only the
 * authenticated app shell (AppLayout) and portal-rendered popovers stamp
 * `data-theme` onto their own root node — see global.css's
 * `[data-theme="light"]` overrides. This keeps Login/ResetCode/ResetPw (which
 * render outside AppLayout) on their own fixed light-card-on-dark-background
 * design regardless of the app theme a user last chose.
 */
const STORAGE_KEY = 'gaussiq-theme'
const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark'
    } catch {
      return 'dark'
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // localStorage unavailable (private mode, etc.) — theme just won't persist
    }
  }, [theme])

  function toggleTheme() {
    setTheme(current => (current === 'dark' ? 'light' : 'dark'))
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

// Co-located provider + hook (as in the design guide's AuthContext). The Fast
// Refresh rule wants component-only files; the hook export is a deliberate exception.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
